import { z } from 'zod'
import typeguard from '../typeguard'
import {
  landingPageManagedShopSchema,
  landingPageSettingsShopsSchema,
  landingPageShopSettingsSchema,
} from '../dbTypes/landingPageSettings'

export const landingPageTemplateResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  values: landingPageShopSettingsSchema,
  created: z.union([z.string(), z.date()]).optional(),
  changed: z.union([z.string(), z.date()]).optional(),
})
export type landingPageTemplateResponse = z.infer<typeof landingPageTemplateResponseSchema>

export const landingPageSettingsResponseSchema = z.object({
  shops: landingPageSettingsShopsSchema,
  presets: z.array(landingPageTemplateResponseSchema).optional(),
  changed: z.string().optional(),
})
export type landingPageSettingsResponse = z.infer<typeof landingPageSettingsResponseSchema>
export const isLandingPageSettingsResponse = typeguard<landingPageSettingsResponse>(landingPageSettingsResponseSchema)

export const landingPageImageReferenceSchema = z.object({
  source: z.string(),
  label: z.string(),
  context: z.string().optional(),
})
export type landingPageImageReference = z.infer<typeof landingPageImageReferenceSchema>

export const landingPageImageSchema = z.object({
  name: z.string(),
  url: z.string(),
  aiGenerated: z.boolean().optional(),
  size: z.number().optional(),
  created: z.string().optional(),
  changed: z.string().optional(),
  isNew: z.boolean().optional(),
  used: z.boolean().optional(),
  usageTypes: z.array(z.string()).optional(),
  references: z.array(landingPageImageReferenceSchema).optional(),
})
export type landingPageImage = z.infer<typeof landingPageImageSchema>

// Public responses use the canonical Landingpage asset URL as key and include
// only positively marked assets.  An omitted key therefore always means that
// the asset is not marked as AI generated.
export const landingPageImageAiGeneratedSchema = z.record(z.literal(true))
export type landingPageImageAiGenerated = z.infer<typeof landingPageImageAiGeneratedSchema>

export const landingPageImagesResponseSchema = z.object({
  shopId: landingPageManagedShopSchema,
  images: z.array(landingPageImageSchema),
  uploaded: landingPageImageSchema.optional(),
})
export type landingPageImagesResponse = z.infer<typeof landingPageImagesResponseSchema>
export const isLandingPageImagesResponse = typeguard<landingPageImagesResponse>(landingPageImagesResponseSchema)

export const landingPageRebuildStatusSchema = z.object({
  shopId: landingPageManagedShopSchema,
  status: z.enum(['idle', 'running', 'success', 'error']),
  buildMode: z.enum(['quick', 'cache', 'full']).optional(),
  progress: z.number().min(0).max(100).optional(),
  step: z.string().optional(),
  message: z.string().optional(),
  startedAt: z.string().optional(),
  finishedAt: z.string().optional(),
  logs: z.array(z.string()).optional(),
})
export type landingPageRebuildStatus = z.infer<typeof landingPageRebuildStatusSchema>
export const isLandingPageRebuildStatus = typeguard<landingPageRebuildStatus>(landingPageRebuildStatusSchema)

export const landingPageProductPickerOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
  subtitle: z.string().optional(),
  imageSrc: z.string().optional(),
  productId: z.number().optional(),
  brandTitle: z.string().optional(),
  productOptions: z.string().optional(),
  productType: z.string().optional(),
  price: z.number().optional(),
  stock: z.number().optional(),
  hasImage: z.boolean().optional(),
})
export type landingPageProductPickerOption = z.infer<typeof landingPageProductPickerOptionSchema>

export const landingPageProductPickerOptionsResponseSchema = z.object({
  shopId: landingPageManagedShopSchema,
  options: z.array(landingPageProductPickerOptionSchema),
})
export type landingPageProductPickerOptionsResponse = z.infer<typeof landingPageProductPickerOptionsResponseSchema>
export const isLandingPageProductPickerOptionsResponse = typeguard<landingPageProductPickerOptionsResponse>(landingPageProductPickerOptionsResponseSchema)
